import { useCallback, useEffect, useState } from "react";
import { ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceDot } from "recharts";
import { Microscope, ArrowDownRight, ArrowUpRight, Minus } from "@/lib/icons";
import { api } from "@/lib/api";
import { colorOf } from "@/lib/habitColors";

const WEEK_OPTS = [4, 8, 12, 26];
const fmt = (d) => new Date(d + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });

function Stat({ label, value, sub, tone = "text-stone-900", testid }) {
  return (
    <div className="rounded-2xl bg-stone-50 border border-stone-200 p-3" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{label}</div>
      <div className={`font-mono text-2xl font-bold ${tone}`}>{value ?? "—"}</div>
      {sub && <div className="text-[11px] text-stone-500">{sub}</div>}
    </div>
  );
}

function Verdict({ data }) {
  const { compare: c, metrics, metric, habits, habit_id } = data;
  const m = metrics.find((x) => x.key === metric);
  const h = habits.find((x) => x.id === habit_id);
  if (!h) return null;
  if (c.diff == null) return <p className="text-sm text-stone-500" data-testid="explore-verdict">Not enough overlapping days yet — the member needs wellbeing check-ins on both habit days and non-habit days.</p>;
  const better = m?.better;
  const good = better ? (better === "high" ? c.diff > 0 : c.diff < 0) : null;
  const Icon = c.diff > 0 ? ArrowUpRight : c.diff < 0 ? ArrowDownRight : Minus;
  return (
    <div className={`rounded-2xl p-4 border ${good === true ? "bg-emerald-50 border-emerald-200" : good === false ? "bg-amber-50 border-amber-200" : "bg-stone-50 border-stone-200"}`} data-testid="explore-verdict">
      <div className="flex items-start gap-2">
        <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${good === true ? "text-emerald-700" : good === false ? "text-amber-700" : "text-stone-500"}`} />
        <div className="text-sm text-stone-800">
          On days <strong>{h.title}</strong> was logged, <strong>{m?.label.toLowerCase()}</strong> averaged <strong>{c.with_avg}</strong> vs <strong>{c.without_avg}</strong> on other days
          ({c.diff > 0 ? "+" : ""}{c.diff}, n = {c.n_with}/{c.n_without}).
          {c.next_day_with_avg != null && c.next_day_without_avg != null && <> Next-day effect: {c.next_day_with_avg} vs {c.next_day_without_avg}.</>}
          {!c.reliable && <span className="block text-[11px] text-stone-500 mt-1">Fewer than 5 days on one side — treat as a hint, not a finding. Correlation, not causation.</span>}
        </div>
      </div>
    </div>
  );
}

export function ExploreTool({ clientId, onParams }) {
  const [metric, setMetric] = useState("mood");
  const [habitId, setHabitId] = useState("");
  const [weeks, setWeeks] = useState(8);
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const r = await api.get(`/clinician/client/${clientId}/explore`, { params: { metric, habit_id: habitId || undefined, weeks } });
    setData(r.data);
    if (!habitId && r.data.habit_id) setHabitId(r.data.habit_id);
    onParams?.({ metric, habit_id: r.data.habit_id, weeks });
  }, [clientId, metric, habitId, weeks, onParams]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const habit = data.habits.find((h) => h.id === data.habit_id);
  const c = colorOf(habit);
  const m = data.metrics.find((x) => x.key === metric);
  const chart = data.series.map((p) => ({ ...p, label: fmt(p.date), habitBar: p.done ? (m?.kind === "count" ? 1 : 10) : 0 }));
  const sel = "px-3 py-2 rounded-xl border border-stone-300 bg-white text-sm outline-none focus:border-amber-600";
  return (
    <section className="warm-card p-6" data-testid="explore-tool">
      <div className="flex items-center gap-2 mb-1"><Microscope className="w-5 h-5 text-amber-700" /><h3 className="font-display text-lg font-bold text-stone-900">Explore: symptom × habit</h3></div>
      <p className="text-xs text-stone-500 mb-4">Pick a tracked measure and a habit. The line is the daily measure; shaded bars mark days the habit was logged. Weekly table below shows habit formation against the weekly average.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        <select value={metric} onChange={(e) => setMetric(e.target.value)} className={sel} data-testid="explore-metric">
          {data.metrics.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
        </select>
        <select value={data.habit_id || ""} onChange={(e) => setHabitId(e.target.value)} className={sel} data-testid="explore-habit">
          {data.habits.map((h) => <option key={h.id} value={h.id}>{h.title}</option>)}
        </select>
        <div className="flex gap-1 rounded-xl bg-stone-100 p-1">{WEEK_OPTS.map((w) => <button key={w} onClick={() => setWeeks(w)} data-testid={`explore-weeks-${w}`} className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${weeks === w ? "bg-stone-900 text-white" : "text-stone-600"}`}>{w}w</button>)}</div>
      </div>
      <div className="h-64" data-testid="explore-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chart} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid stroke="#e7e5e4" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={Math.max(0, Math.floor(chart.length / 8) - 1)} />
            <YAxis domain={m?.kind === "count" ? [0, "auto"] : [0, 10]} tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v, name) => (name === "habitBar" ? [v ? "logged" : "—", habit?.title] : [v, m?.label])} labelFormatter={(l) => l} />
            <Bar dataKey="habitBar" fill={c.hex} opacity={0.3} isAnimationActive={false} />
            <Line type="monotone" dataKey="value" stroke="#276539" strokeWidth={2} dot={{ r: 2.5 }} connectNulls isAnimationActive={false} />
            {chart.filter((p) => p.done && p.value != null).map((p) => <ReferenceDot key={p.date} x={p.label} y={p.value} r={4} fill="#059669" stroke="white" />)}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="grid sm:grid-cols-4 gap-3 mt-4">
        <Stat label={`${m?.label} · habit days`} value={data.compare.with_avg} sub={`${data.compare.n_with} days`} tone="text-emerald-800" testid="explore-with" />
        <Stat label={`${m?.label} · other days`} value={data.compare.without_avg} sub={`${data.compare.n_without} days`} testid="explore-without" />
        <Stat label="Difference" value={data.compare.diff != null ? `${data.compare.diff > 0 ? "+" : ""}${data.compare.diff}` : null} sub={data.compare.reliable ? "≥5 days each side" : "small sample"} testid="explore-diff" />
        <Stat label="Check-ins in range" value={data.days_logged} sub={`of ${data.series.length} days`} testid="explore-logged" />
      </div>
      <div className="mt-4"><Verdict data={data} /></div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs" data-testid="explore-weekly">
          <thead><tr className="text-left text-stone-500"><th className="py-1 pr-3 font-semibold">Week of</th><th className="py-1 pr-3 font-semibold">{habit?.title} days</th><th className="py-1 pr-3 font-semibold">{m?.label} avg</th><th className="py-1 font-semibold">Check-ins</th></tr></thead>
          <tbody>
            {data.weekly.map((w) => (
              <tr key={w.week_start} className="border-t border-stone-100">
                <td className="py-1.5 pr-3 font-mono">{fmt(w.week_start)}</td>
                <td className="py-1.5 pr-3"><span className="inline-flex gap-0.5 align-middle">{Array.from({ length: 7 }, (_, i) => <span key={i} className={`w-2.5 h-2.5 rounded-sm ${i < w.habit_days ? c.dot : "bg-stone-200"}`} />)}</span> <span className="ml-1 font-mono">{w.habit_days}</span></td>
                <td className="py-1.5 pr-3 font-mono">{w.metric_avg ?? "—"}</td>
                <td className="py-1.5 font-mono">{w.logged_days}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
